import {TheRave} from "../TheRave";
import {Layers, TheRaveLayers} from "../view/TheRaveLayers";
import {FontStyleConfig} from "../config/FontConfig";
import {TimelineLite, TweenLite, Power2} from "gsap";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {TheRaveReelView} from "../reels/TheRaveReelView";
import {BitmapText} from "pixi.js";

export class StickySymbol extends PIXI.Container {
    private _stickySymbol:any;
    private _currentSprite: PIXI.Sprite;
    private _container: PIXI.Container;
    private _enhancerMultiplierContainer: PIXI.Container;
    private _multiplierHolder: PIXI.Sprite;
    private multipliers: PIXI.BitmapText[];
    private enhancerMultipliers: PIXI.BitmapText[];

    constructor() {
        super();
        this._container = new PIXI.Container();
        this._enhancerMultiplierContainer = new PIXI.Container();
        this._enhancerMultiplierContainer.visible = false;
        this._currentSprite = new PIXI.Sprite(TheRave.assetLoader.getTextures("djAnimationBG")[0]);
        this._currentSprite.name = `djAnimationBG`;
        this._currentSprite.anchor.set(0.5, 1);
        this._currentSprite.position.set(363, 246);
        TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.addChild(this._currentSprite);

        this._stickySymbol = {
            "NORMAL": this.createStickySymbol("NORMAL"),
            "FREESPIN": this.createStickySymbol("FREESPIN"),
            "FREESPIN_SUPER": this.createStickySymbol("FREESPIN_SUPER")
        };

        this._multiplierHolder = new PIXI.Sprite();
        this._multiplierHolder.name = `djMultiplierHolder`;
        this._multiplierHolder.anchor.set(0.5, 0);
        this._multiplierHolder.position.set(358, 177);
        TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.addChild(this._multiplierHolder);
        this.multipliers = ArrayHelper.initArrayWithValues(3, (i: number) => this.addMultiplier(i));
        this.enhancerMultipliers = ArrayHelper.initArrayWithValues(3, (i: number) => this.addEnhancerMultiplier(i));
    }

    private addMultiplier(index: number): PIXI.BitmapText {
        const multiplier: PIXI.BitmapText = new PIXI.BitmapText("", FontStyleConfig.MULTIPLIER_M1_FS);
        (<PIXI.Point>multiplier.anchor).set(0.5, 1);
        multiplier.position.set(210 + 150 * index, 215);
        this._container.addChild(multiplier);
        return multiplier;
    }

    private addEnhancerMultiplier(index: number): PIXI.BitmapText {
        const multiplier: PIXI.BitmapText = new PIXI.BitmapText("", FontStyleConfig.ENHANCER_MULTIPLIER);
        (<PIXI.Point>multiplier.anchor).set(0.5, 1);
        multiplier.position.set(230 + TheRaveReelView.SYMBOL_WIDTH * index, 654);
        multiplier.tint = 0x555555;
        this._enhancerMultiplierContainer.addChild(multiplier);
        return multiplier;
    }

    public showMultiplier(): TimelineLite {
        this._container.alpha = 0;
        this._container.visible = true;
        this._currentSprite.addChild(this._container);
        TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.addChild(this._container);
        const tl = new TimelineLite();
        this.multipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            multiplierText.text = `x${TheRave.model.currentParsedData.freespinMidMultipliers[i + 1]}`;
            multiplierText.scale.set(0, 0);
            tl.add(TweenLite.fromTo(multiplierText.scale, {x: 1.1, y: 1.1}, {
                x: 1,
                y: 1,
                duration: 0.2,
                ease: Power2.easeOut
            }), 0.1);
        });
        this.enhancerMultipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            multiplierText.text = `x${TheRave.model.currentParsedData.freespinMidMultipliers[i + 1]}`;
        });
        tl.add(TweenLite.to(this._container, {duration: 0.5, alpha: 1, ease: `power3.out`}));
        tl.add(() => {
            TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)!.addChild(this._enhancerMultiplierContainer);
            this._enhancerMultiplierContainer.visible = true;
        });
        return tl;
    }

    public onRestore(): void {
        this._container.alpha = 1;
        this._container.visible = true;
        this._currentSprite.addChild(this._container);
        TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.addChild(this._container);

        this.multipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            multiplierText.text = `x${TheRave.model.currentParsedData.freespinMidMultipliers[i + 1]}`;
            multiplierText.scale.set(1, 1);
        });
        this.enhancerMultipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            const isActive:boolean = (TheRave.model.currentParsedData.reels[i + 1][3][0] === "M");
            multiplierText.text = `x${TheRave.model.currentParsedData.freespinMidMultipliers[i + 1]}`;
            multiplierText.tint =  isActive? 0xffffff : 0x555555;
            multiplierText.alpha = isActive? 1 : 0.5;
        });
        TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)!.addChild(this._enhancerMultiplierContainer);
        this._enhancerMultiplierContainer.visible = true;
    }

    public update(): TimelineLite {
        const tl = new TimelineLite();
        this.multipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            if (parseInt(multiplierText.text) != TheRave.model.parsedGameData.freespinMidMultipliers[i + 1]) {
                tl.add(this.updateSingleMultiplier(i + 1), 0);
            }
        });
        this.enhancerMultipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            if (parseInt(multiplierText.text) != TheRave.model.parsedGameData.freespinMidMultipliers[i + 1]) {
                tl.add(this.updateSingleMultiplier(i + 1), 0);
            }
        });
        return tl;
    }

    public updateSingleMultiplier(reelId: number): TimelineLite {
        if (reelId < 1 || reelId > 3) {
            return new TimelineLite();
        }
        const tl = new TimelineLite(),
            multiplierText: PIXI.BitmapText = this.multipliers[reelId - 1],
            enhancerMultiplierText: PIXI.BitmapText = this.enhancerMultipliers[reelId - 1],
            startTime: number = 0,
            duration: number = 0.3;

        tl.add([
            TweenLite.fromTo(multiplierText.scale, {x: 1.1, y: 1.1}, {
                x: 1,
                y: 1,
                duration: duration,
                ease: Power2.easeOut
            }),
            TweenLite.fromTo(enhancerMultiplierText.scale, {x: 1.1, y: 1.1}, {
                x: 1,
                y: 1,
                duration: duration,
                ease: Power2.easeOut
            })], startTime);
        tl.add(() => {multiplierText.text = `x${TheRave.model.parsedGameData.freespinMidMultipliers[reelId]}`}, startTime + duration);
        tl.add(() => {enhancerMultiplierText.text = `x${TheRave.model.parsedGameData.freespinMidMultipliers[reelId]}`}, startTime + duration);

        return tl;
    }

    public hideMultiplier(): void {
        this._container.visible = false;
        this._enhancerMultiplierContainer.visible = false;
        TheRaveLayers.getLayer(Layers.FOREGROUND_TOP)!.removeChild(this._enhancerMultiplierContainer);
    }

    public show(mode:string): void {
        for (let key in this._stickySymbol) {
            const value: PIXI.spine.Spine = this._stickySymbol[key];
            if (key === mode) {
                let animName:string=JSON.parse(JSON.stringify(mode));
                if(TheRave.apiPlugin.gameClientConfiguration.useCensoredGfxUS){
                    animName=animName+"_US_animation";
                }
                TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.addChild(value);
                value.state.setAnimation(0, animName,true);
                value.visible = true;
            } else {
                if (value.visible) {
                    TheRaveLayers.getLayer(Layers.STICKY_SYMBOL)!.removeChild(value);
                }
                value.visible = false;
            }
        }
    }

    private showNormal(): void {}

    public createStickySymbol(mode:string): PIXI.spine.Spine {
        const sprite: PIXI.spine.Spine = new PIXI.spine.Spine(TheRave.assetLoader.getSpineAsset("DJBOOTH_TOP"));
        sprite.name = `DJAnimation${mode}`;
        sprite.position.set(360, 87);
        return sprite;
    }

    public isNormal(): void {}

    public reset(): void {}

    public setMultiplierActive(reel: number) {
        this.enhancerMultipliers[reel - 1].tint = 0xffffff;
        this.enhancerMultipliers[reel - 1].alpha = 1;
    }

    public setAllMultiplierInActive() {
        this.enhancerMultipliers.forEach((multiplier: BitmapText) => {
            multiplier.tint = 0x555555;
            multiplier.alpha = 0.5;
        })
    }

    public verifyTotalMultiplier(): void {
        if (!NolimitConfig.isDevMode) {
            return;
        }

        this.enhancerMultipliers.forEach((multiplierText: PIXI.BitmapText, i: number) => {
            if (multiplierText.text != `x${TheRave.model.parsedGameData.freespinMidMultipliers[i + 1]}`) {
                debugger;
                throw new Error("Displaying Multiplier doesn't match server data.");

            }
        });

    }
}